import { useState } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';

const ScanAmbiguousDialog = ({
  open,
  onOpenChange,
  candidates = [],
  scannedCode = '',
  getItemId,
  renderAmbiguousRow,
  title = '請選擇',
  description,
  confirmText = '加入',
  onConfirm,
  onCancel,
}) => {
  const [selectedIdDraft, setSelectedIdDraft] = useState(null);
  const selectedId = open ? selectedIdDraft : null;

  function resolveId(item, index) {
    const id = getItemId?.(item);
    if (id) {
      return `${id}-${index}`;
    }
    return `scanned-${index}`;
  }

  function handleOpenChange(nextOpen) {
    if (!nextOpen) {
      setSelectedIdDraft(null);
    }
    onOpenChange(nextOpen);
  }

  function handleCancel() {
    setSelectedIdDraft(null);
    onCancel?.();
    onOpenChange(false);
  }

  function handleConfirm() {
    const row = candidates.find((item, index) => resolveId(item, index) === selectedId);
    if (!row) {
      return;
    }
    onConfirm?.(row);
    setSelectedIdDraft(null);
    onOpenChange(false);
  }

  const resolvedDescription = description
    || (scannedCode
      ? `「${scannedCode}」對應多筆，請單選一筆加入。`
      : '對應多筆結果，請單選一筆加入。');

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description={resolvedDescription}
      headerVariant="primary"
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      confirmDisabled={!selectedId}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText={confirmText}
    >
      <ul className="m-0 list-none space-y-2 p-0">
        {candidates.map((row, index) => {
          const itemId = resolveId(row, index);
          const inputId = `scan-ambiguous-${itemId}`;
          const checked = selectedId === itemId;

          return (
            <li key={itemId}>
              <label
                htmlFor={inputId}
                className={cn(
                  'px-3 py-3',
                  'border border-slate-200 rounded-md',
                  'flex cursor-pointer items-start gap-3',
                  'transition-colors',
                  checked && 'border-primary-400 bg-primary-50 ring-2 ring-primary-200',
                  !checked && 'hover:bg-slate-50',
                )}
              >
                <input
                  id={inputId}
                  type="radio"
                  name="scan-ambiguous"
                  className="mt-1 size-4 shrink-0 accent-primary"
                  checked={checked}
                  onChange={() => setSelectedIdDraft(itemId)}
                />
                <div className="min-w-0 flex-1">
                  {renderAmbiguousRow?.(row, { scannedCode, checked })}
                </div>
              </label>
            </li>
          );
        })}
      </ul>
    </AppDialog>
  );
};

ScanAmbiguousDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  candidates: PropTypes.arrayOf(PropTypes.object),
  scannedCode: PropTypes.string,
  getItemId: PropTypes.func.isRequired,
  renderAmbiguousRow: PropTypes.func.isRequired,
  title: PropTypes.string,
  description: PropTypes.node,
  confirmText: PropTypes.string,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
};

export default ScanAmbiguousDialog;
